import { useCallback, useEffect, useRef, useState } from 'react'
import { AlertTriangle, CircleCheck, CircleX, Info, LoaderCircle, RefreshCw, XCircle } from 'lucide-react'
import {
  MEER_BVS, NAAM_VAN, RECHT_VERSTUREN,
  type BoekingGegevens, type ExactVelden, type GrootboekGegevens, type KostenplaatsGegevens, type VerkoopfactuurGegevens, type Voorstel,
} from '../../../gedeeld/types'
import { c } from '../../lib/api'
import { invoerDatumLang } from '../../lib/opmaak'
import { meld, meldFout, useMag } from '../../lib/staat'
import { Knop } from '../ui'
import { ExactBoeking } from './ExactBoeking'
import { euro, naamVan } from './ExactDelen'
import { ExactGrootboek, ExactKostenplaats } from './ExactGrootboek'
import { ExactVerkoopfactuur } from './ExactVerkoopfactuur'

/* ===========================================================================
 *  Een voorstel om in Exact te schrijven, als kaart (0219)
 *
 *  Grootboekrekeningen, kostenplaatsen, boekingen en verkoopfacturen. Net
 *  als bij relaties schrijft de AI niets: hij zet deze kaart klaar, jij
 *  past aan, en pas Uitvoeren schrijft naar Exact -- de echte boeken van de
 *  bv's van Truckwash 1 Group. Daarom drie remmen:
 *
 *  1. Na elke wijziging kijkt de server de kaart opnieuw na
 *     (exact-controleren, ~800 ms nadat je stopt met typen). Uitvoeren kan
 *     pas als die controle terug is, bij precies wat er NU staat, en zonder
 *     fouten. Een antwoord op een oudere stand wordt weggegooid: wie snel
 *     typt, krijgt antwoorden in de verkeerde volgorde terug, en het
 *     antwoord op "4130" mag niet over "4135" heen.
 *  2. Uitvoeren vraagt een tweede klik, met in woorden wat er gaat gebeuren.
 *  3. De server kijkt bij Uitvoeren hoe dan ook alles nog een keer na, met
 *     het recht van dat moment. Wat hier staat is om te ZIEN; vertrouwen doet
 *     alleen de server.
 * =========================================================================== */

const WACHT_MS = 800
/** Na zoveel tijd vervalt de eerste klik op Uitvoeren weer. */
const BEVESTIG_MS = 10000

const sleutelVan = (x: ExactVelden) => JSON.stringify([x.gegevens, [...x.divisies].sort()])
const bvs = (n: number) => `${n} bv${n === 1 ? '' : "'s"}`

/** Wat er op de knop staat: wat er gaat gebeuren, niet "Uitvoeren". */
function knopTekst(v: ExactVelden): string {
  const n = v.divisies.length
  switch (v.functie) {
    case 'grootboek': {
      const g = v.gegevens as GrootboekGegevens
      return `Rekening ${g.actie} in ${bvs(n)}`
    }
    case 'kostenplaats': {
      const g = v.gegevens as KostenplaatsGegevens
      return `${g.soort === 'kostendrager' ? 'Kostendrager' : 'Kostenplaats'} ${g.actie} in ${bvs(n)}`
    }
    case 'verkoopfactuur':
      return (v.gegevens as VerkoopfactuurGegevens).versturen ? 'Factuur maken en mailen' : 'Factuur maken in Exact'
    default:
      return `${NAAM_VAN[v.functie]} maken in Exact`
  }
}

/** De zin bij de tweede klik: in gewone woorden wat er gaat gebeuren, en waar. */
function bevestigZin(v: ExactVelden): string {
  const namen = v.divisies.map((d) => v.mogelijk.find((m) => m.code === d)?.naam ?? d)
  const waar = namen.length <= 3 ? namen.join(', ') : `${namen.slice(0, 2).join(', ')} en nog ${namen.length - 2}`
  if (v.functie === 'grootboek') {
    const g = v.gegevens as GrootboekGegevens
    return `Rekening ${g.code} ${g.actie} in ${waar}.`
  }
  if (v.functie === 'kostenplaats') {
    const g = v.gegevens as KostenplaatsGegevens
    return `${g.soort === 'kostendrager' ? 'Kostendrager' : 'Kostenplaats'} ${g.code} ${g.actie} in ${waar}.`
  }
  const t = v.berekend?.totalen
  const incl = t?.incl ?? t?.debet ?? (t?.saldo !== undefined ? Math.abs(t.saldo) : undefined)
  const bedrag = incl !== undefined ? `, ${euro(incl, (v.gegevens as BoekingGegevens).valuta)}` : ''
  if (v.functie === 'verkoopfactuur') {
    const g = v.gegevens as VerkoopfactuurGegevens
    return `Een factuur aan ${naamVan(v.berekend, 'relatie', g.klant) ?? g.klant}${bedrag} in ${waar}.${g.versturen ? ' Exact mailt hem meteen naar de klant.' : ''}`
  }
  return `${NAAM_VAN[v.functie]}${bedrag} in ${waar}. Dit komt in de echte boekhouding.`
}

export default function ExactFormulier({ berichtId, voorstel, onVoorstel }: {
  berichtId: string
  voorstel: Voorstel & { velden: ExactVelden }
  onVoorstel: (v: Voorstel) => void
}) {
  const [v, setV] = useState<ExactVelden>(() => structuredClone(voorstel.velden))
  const sleutel = sleutelVan(v)
  /* De stand waar v.controle bij hoort. Het voorstel kwam nagekeken binnen
     (voorbereiden), dus in het begin is dat de stand van nu. */
  const [gezien, setGezien] = useState(sleutel)
  const [controleert, setControleert] = useState(false)
  const [controleFout, setControleFout] = useState<string | null>(null)
  const [ongeldig, setOngeldig] = useState<string[]>([])
  const [bezig, setBezig] = useState<'uitvoeren' | 'afwijzen' | null>(null)
  /* De stand van de kaart bij de eerste klik op Uitvoeren. Verandert er
     daarna iets, dan geldt die klik niet meer. */
  const [bevestig, setBevestig] = useState<string | null>(null)
  const magVersturen = useMag(RECHT_VERSTUREN)
  const verouderd = sleutel !== gezien

  const nu = useRef(v)
  nu.current = v
  /* Elk verzoek een nummer; alleen het antwoord op het laatste telt. */
  const volgnr = useRef(0)
  const levend = useRef(true)
  const klok = useRef<ReturnType<typeof setTimeout> | null>(null)
  /* Ook weer op true bij het (opnieuw) koppelen: StrictMode koppelt in dev
     los en weer aan, en dan bleef dit false en werd elk antwoord genegeerd. */
  useEffect(() => {
    levend.current = true
    return () => { levend.current = false; if (klok.current) clearTimeout(klok.current) }
  }, [])

  async function controleer() {
    if (klok.current) { clearTimeout(klok.current); klok.current = null }
    const n = ++volgnr.current
    const verstuurd = nu.current
    const verstuurdeSleutel = sleutelVan(verstuurd)
    setControleert(true)
    try {
      const { velden } = await c('exact-controleren', { berichtId, voorstelId: voorstel.id, velden: { ...verstuurd, uitkomsten: undefined } })
      if (!levend.current || n !== volgnr.current) return
      /* Intussen veranderd: dit antwoord gaat over een oude stand. Niet
         toepassen; de wachttijd die bij die wijziging begon, stuurt zo een
         nieuw verzoek. */
      if (sleutelVan(nu.current) !== verstuurdeSleutel) return
      /* Functie en "waar mag je dit" neemt de server uit het opgeslagen
         voorstel; die horen hier niet te veranderen, ook niet als een
         antwoord ze anders of leeg terugstuurt. */
      const nieuw: ExactVelden = {
        ...nu.current, ...velden,
        functie: nu.current.functie,
        mogelijk: velden.mogelijk?.length ? velden.mogelijk : nu.current.mogelijk,
        controle: velden.controle ?? { fouten: [], waarschuwingen: [] },
        uitkomsten: undefined,
      }
      setV(nieuw)
      setGezien(sleutelVan(nieuw))
      setControleFout(null)
    } catch (e) {
      if (!levend.current || n !== volgnr.current) return
      setControleFout(e instanceof Error ? e.message : String(e))
    } finally {
      if (levend.current && n === volgnr.current) setControleert(false)
    }
  }

  /* Na een wijziging: even wachten tot je klaar bent met typen. */
  useEffect(() => {
    if (klok.current) { clearTimeout(klok.current); klok.current = null }
    if (sleutel === gezien) return
    klok.current = setTimeout(() => { klok.current = null; void controleer() }, WACHT_MS)
  }, [sleutel]) // eslint-disable-line react-hooks/exhaustive-deps

  /* De eerste klik vervalt vanzelf: een "weet je het zeker" die een kwartier
     blijft staan, klik je later per ongeluk weg. */
  useEffect(() => {
    if (!bevestig) return
    const t = setTimeout(() => setBevestig(null), BEVESTIG_MS)
    return () => clearTimeout(t)
  }, [bevestig])

  const meldOngeldig = useCallback((s: string) => (ja: boolean) => {
    setOngeldig((l) => (ja ? (l.includes(s) ? l : [...l, s]) : l.filter((x) => x !== s)))
  }, [])

  const fouten = v.controle?.fouten ?? []
  const waarschuwingen = v.controle?.waarschuwingen ?? []
  const uitleg = v.uitleg ?? []
  const verstuurtZonderRecht = v.functie === 'verkoopfactuur' && (v.gegevens as VerkoopfactuurGegevens).versturen && !magVersturen

  /* Waarom Uitvoeren (nog) niet kan; null = het kan. */
  const reden: string | null =
    bezig ? null
      : ongeldig.length ? 'Een getal klopt niet; kijk naar het rode veld.'
        : !v.divisies.length ? 'Kies eerst een bv.'
          : !MEER_BVS[v.functie] && v.divisies.length > 1 ? 'Kies precies één bv.'
            : verstuurtZonderRecht ? 'Mailen naar de klant mag jij niet; zet dat vinkje uit.'
              : verouderd && controleFout ? 'De controle lukte niet; probeer opnieuw te controleren.'
                : verouderd || controleert ? 'Even wachten: de kaart wordt nagekeken.'
                  : fouten.length ? 'Los eerst de fouten bovenaan op.'
                    : null
  const bevestigd = bevestig !== null && bevestig === sleutel && !reden

  async function besluit(b: 'uitvoeren' | 'afwijzen') {
    if (b === 'uitvoeren' && reden) return
    setBezig(b)
    try {
      const { voorstel: nieuw } = await c('voorstel', {
        berichtId, voorstelId: voorstel.id, besluit: b,
        ...(b === 'uitvoeren' ? { velden: { ...v, uitkomsten: undefined } } : {}),
      })
      onVoorstel(nieuw)
      if (nieuw.status === 'uitgevoerd') {
        const uit = (nieuw.velden as ExactVelden).uitkomsten ?? []
        const mis = uit.filter((u) => !u.ok).length
        const half = uit.filter((u) => u.ok && u.fout).length
        if (mis) meld(`In ${bvs(mis)} lukte het niet; kijk op de kaart.`, 'fout')
        else if (half) meld('Gedaan in Exact, maar niet alles lukte; kijk op de kaart.', 'fout')
        else meld('Gedaan in Exact.')
      }
      if (nieuw.status === 'mislukt') meld(nieuw.uitkomst ?? 'Uitvoeren lukte niet.', 'fout')
    } catch (e) {
      meldFout(e)
    } finally {
      if (levend.current) { setBezig(null); setBevestig(null) }
    }
  }

  const label = knopTekst(v)
  const bezigMetControle = controleert || (verouderd && !controleFout)
  const props = { v, onV: setV }

  return (
    <>
      <div className="formulier compact relatie-kaart ex-kaart">
        <div className="breed ex-meldingen" aria-live="polite">
          {controleFout && (
            <div className="ex-fouten"><p><XCircle size={13} /> Nakijken lukte niet: {controleFout}</p></div>
          )}
          {fouten.length > 0 && (
            <div className={`ex-fouten ${verouderd ? 'oud' : ''}`}>
              {fouten.map((f) => <p key={f}><XCircle size={13} /> {f}</p>)}
            </div>
          )}
          {waarschuwingen.length > 0 && (
            <div className={`relatie-waarschuwing ${verouderd ? 'oud' : ''}`}>
              {waarschuwingen.map((w) => <p key={w}><AlertTriangle size={13} /> {w}</p>)}
            </div>
          )}
          {uitleg.length > 0 && (
            <ul className={`ex-uitleg ${verouderd ? 'oud' : ''}`}>
              {uitleg.map((u) => <li key={u}><Info size={12} /> {u}</li>)}
            </ul>
          )}
        </div>

        {v.functie === 'grootboek' && <ExactGrootboek {...props} g={v.gegevens as GrootboekGegevens} />}
        {v.functie === 'kostenplaats' && <ExactKostenplaats {...props} g={v.gegevens as KostenplaatsGegevens} />}
        {(v.functie === 'inkoop' || v.functie === 'verkoop' || v.functie === 'memoriaal' || v.functie === 'bank' || v.functie === 'kas') && (
          <ExactBoeking {...props} g={v.gegevens as BoekingGegevens} gecontroleerd={!verouderd} oud={bezigMetControle} meldOngeldig={meldOngeldig} />
        )}
        {v.functie === 'verkoopfactuur' && (
          <ExactVerkoopfactuur {...props} g={v.gegevens as VerkoopfactuurGegevens} gecontroleerd={!verouderd} oud={bezigMetControle}
            meldOngeldig={meldOngeldig} magVersturen={magVersturen} />
        )}

        <div className="breed relatie-opnieuw">
          <span className="ex-status" role="status">
            {bezigMetControle
              ? <><LoaderCircle size={12} className="draai" /> controleren…</>
              : !controleFout && <><CircleCheck size={12} /> nagekeken</>}
          </span>
          <Knop klein icoon={<RefreshCw size={13} />} bezig={controleert} onClick={() => void controleer()}>
            Opnieuw controleren
          </Knop>
        </div>
      </div>

      <footer className="voorstel-voet ex-voet">
        {reden && <span className="ex-reden" id={`${voorstel.id}-reden`}>{reden}</span>}
        {bevestigd && <span className="ex-bevestig" role="alert">Zeker weten? {bevestigZin(v).replace(/\.\.(?=\s|$)/g, '.') /* "B.V.." */}</span>}
        <div className="ex-voet-knoppen">
          {bevestigd
            ? <Knop soort="stil" onClick={() => setBevestig(null)} disabled={!!bezig}>Nee, nog niet</Knop>
            : <Knop soort="stil" onClick={() => void besluit('afwijzen')} bezig={bezig === 'afwijzen'} disabled={!!bezig}>Afwijzen</Knop>}
          <Knop soort="hoofd" bezig={bezig === 'uitvoeren'} disabled={!!bezig || !!reden}
            aria-describedby={reden ? `${voorstel.id}-reden` : undefined}
            onClick={() => (bevestigd ? void besluit('uitvoeren') : setBevestig(sleutel))}>
            {bevestigd ? `Ja: ${label.charAt(0).toLowerCase()}${label.slice(1)}` : label}
          </Knop>
        </div>
      </footer>
    </>
  )
}

/** Na afloop: per bv wat er gebeurde, met het nummer uit Exact. */
export function ExactSamenvatting({ v }: { v: ExactVelden }) {
  const uit = v.uitkomsten ?? []
  return (
    <>
      <dl className="voorstel-dl">
        <dt>{NAAM_VAN[v.functie]}</dt><dd>{korteZin(v)}</dd>
      </dl>
      {uit.length > 0 && (
        <ul className="ex-uitkomsten">
          {uit.map((u) => (
            <li key={u.division} className={u.ok ? 'ok' : 'fout'}>
              {u.ok ? <CircleCheck size={13} aria-label="Gelukt" /> : <CircleX size={13} aria-label="Mislukt" />}
              <span>
                <strong>{u.bvNaam}</strong>: {u.wat}
                {u.nummer && <> · <span className="mono">{u.nummer}</span></>}
                {/* ok MET een fout: gemaakt, maar een stap erna niet (een
                    factuur die er staat maar niet verstuurd is). Groen voor
                    wat lukte, oranje voor wat niet. */}
                {u.fout && (u.ok
                  ? <span className="ex-uitkomst-half"><AlertTriangle size={12} aria-hidden /> {u.fout}</span>
                  : <span className="ex-uitkomst-fout">{u.fout}</span>)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}

function korteZin(v: ExactVelden): string {
  switch (v.functie) {
    case 'grootboek': {
      const g = v.gegevens as GrootboekGegevens
      return `${g.code} ${g.omschrijving} (${g.actie})`
    }
    case 'kostenplaats': {
      const g = v.gegevens as KostenplaatsGegevens
      return `${g.soort} ${g.code} ${g.omschrijving} (${g.actie})`
    }
    case 'verkoopfactuur': {
      const g = v.gegevens as VerkoopfactuurGegevens
      const incl = v.berekend?.totalen?.incl
      /* "gemaild" alleen als het mailen ook lukte: een uitkomst kan ok zijn met
         een fout erbij (aangemaakt, niet verstuurd). */
      const gemaild = g.versturen && (v.uitkomsten ?? []).some((u) => u.ok && !u.fout)
      return [naamVan(v.berekend, 'relatie', g.klant) ?? g.klant, invoerDatumLang(g.datum), incl !== undefined ? euro(incl, g.valuta) : null, gemaild ? 'gemaild' : g.versturen ? 'niet gemaild' : null].filter(Boolean).join(' · ')
    }
    default: {
      const g = v.gegevens as BoekingGegevens
      const t = v.berekend?.totalen
      const bedrag = t?.incl ?? t?.debet ?? t?.saldo
      return [g.omschrijving, invoerDatumLang(g.datum), bedrag !== undefined ? euro(bedrag, g.valuta) : null].filter(Boolean).join(' · ')
    }
  }
}
